/**
 * Auth-aware pricing destination — single source of truth for where "Pricing"
 * links and redirects should land across marketing chrome, footers, and CTAs.
 */

export const PUBLIC_PRICING_PATH = "/pricing" as const;
export const AUTHENTICATED_PRICING_PATH = "/billing/pricing" as const;

export type PricingSurface = "public" | "authenticated";

export type PricingDestination = {
  href: typeof PUBLIC_PRICING_PATH | typeof AUTHENTICATED_PRICING_PATH;
  surface: PricingSurface;
  /** User is already on the resolved pricing destination. */
  isCurrent: boolean;
  /** Avoid redundant navigation when already on the active pricing route. */
  suppressNavigation: boolean;
};

export type ResolvePricingDestinationInput = {
  isAuthenticated: boolean;
  pathname?: string | null;
};

export function resolvePricingDestination({
  isAuthenticated,
  pathname = null,
}: ResolvePricingDestinationInput): PricingDestination {
  const href = isAuthenticated
    ? AUTHENTICATED_PRICING_PATH
    : PUBLIC_PRICING_PATH;
  const isCurrent = pathname === href;

  return {
    href,
    surface: isAuthenticated ? "authenticated" : "public",
    isCurrent,
    suppressNavigation: isCurrent,
  };
}

/** Authenticated users must never use the public marketing pricing surface. */
export function shouldRedirectAuthenticatedFromPublicPricing(
  pathname: string,
  isAuthenticated: boolean,
): boolean {
  return isAuthenticated && pathname === PUBLIC_PRICING_PATH;
}

export function isPublicPricingPath(pathname: string): boolean {
  return pathname === PUBLIC_PRICING_PATH;
}

export function isAuthenticatedPricingPath(pathname: string): boolean {
  return (
    pathname === AUTHENTICATED_PRICING_PATH ||
    pathname.startsWith(`${AUTHENTICATED_PRICING_PATH}/`)
  );
}

/** Canonical marketing nav/footer entry key for pricing (guest href is the lookup key). */
export function isPricingNavTarget(href: string): boolean {
  return href === PUBLIC_PRICING_PATH || href === AUTHENTICATED_PRICING_PATH;
}
